import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionsApi, UpdateRoleCapabilityData } from '@/lib/api';
import { useAuth } from './use-auth';

export type CapabilityMap = Record<string, boolean>;

/**
 * Hook to get the full permission matrix (admin only)
 */
export function usePermissionMatrix() {
  return useQuery({
    queryKey: ['permissions', 'matrix'],
    queryFn: async () => {
      const response = await permissionsApi.getMatrix();
      return response.data;
    },
  });
}

/**
 * Hook to get current user's capabilities
 */
export function useMyCapabilities() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['permissions', 'me', user?.id],
    queryFn: async () => {
      const response = await permissionsApi.getMyCapabilities();
      return response.data.capabilities;
    },
    enabled: !!user,
    /** Capabilities rarely change mid-session; window-focus still refetches when stale. */
    staleTime: 120_000,
  });
}

/**
 * Hook to check if current user has a specific capability
 */
export function useHasCapability(capabilityKey: string): boolean {
  const { data: capabilities } = useMyCapabilities();
  return capabilities?.[capabilityKey] ?? false;
}

/**
 * Hook to check multiple capabilities at once
 */
export function useHasCapabilities(capabilityKeys: string[]): Record<string, boolean> {
  const { data: capabilities } = useMyCapabilities();
  const result: Record<string, boolean> = {};
  
  for (const key of capabilityKeys) {
    result[key] = capabilities?.[key] ?? false;
  }
  
  return result;
}

/**
 * Utility function to check capability (for use in components)
 */
export function can(capabilities: CapabilityMap | undefined, key: string): boolean {
  return capabilities?.[key] === true;
}

/**
 * Hook that provides capability checking functions
 */
export function usePermissions() {
  const { data: capabilities, isLoading } = useMyCapabilities();

  return {
    capabilities,
    isLoading,
    hasCapability: (key: string) => capabilities?.[key] === true,
    hasAnyCapability: (keys: string[]) => keys.some((k) => capabilities?.[k] === true),
    hasAllCapabilities: (keys: string[]) => keys.every((k) => capabilities?.[k] === true),
  };
}

/**
 * Utility to check if user has at least one capability.
 */
export function hasAnyCapability(capabilities: CapabilityMap | undefined, keys: string[]): boolean {
  return keys.some((key) => capabilities?.[key] === true);
}

/**
 * Hook to update a role capability (admin only)
 */
export function useUpdateRoleCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateRoleCapabilityData) => {
      const response = await permissionsApi.updateRoleCapability(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}

/**
 * Hook to bulk update role capabilities (admin only)
 */
export function useBulkUpdateRoleCapabilities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: UpdateRoleCapabilityData[]) => {
      const response = await permissionsApi.bulkUpdateRoleCapabilities(updates);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}
